/**
 * web/src/components/gates/CardCandidatesPanel.tsx
 *
 * Three-phase speaker-card panel for the card_assets gate — one phase per
 * artefact the step produces, so all three are visible in the app
 * (2026-07-27 owner ask; 2026-07-16 rework for the board-row output):
 *
 *   Phase "draft" — step 1, the illustration proposals. The PLAIN illustrated
 *   drafts from `gateData.defaults.cardCandidates` render as a grid of
 *   thumbnails (authenticated proxy `GET /api/workflows/:id/card-candidates/
 *   :index`) plus a "Skip card" opt-out. The reviewer picks a draft, picks a
 *   highlight color (event palette swatches + Random), and hits "Create final"
 *   — the parent fires the regenerate mutation with action=finalize, which
 *   paints the colored halo + die-cuts server-side and flips the gate to:
 *
 *   Phase "final" — step 2, the finished illustration (proxy index "final")
 *   previews large with its color chip. The reviewer can re-color in one click
 *   (re-finalize), go back to the drafts, or hit "Build speaker card"
 *   (action=build_card), which composes the deliverables and stages the card
 *   itself in Drive:
 *
 *   Phase "card" — step 3, the SPEAKER CARD (proxy index "card"). This is the
 *   last look: rebuild, go back to the portrait (action=back_to_final), or
 *   Approve — approval publishes V1 + Final + PT HS + Speaker Card to Drive
 *   and the monday board columns, republishing these exact card bytes (no
 *   email — the separate promotion step reads the card back from monday).
 *   A `status: 'unavailable'` preview means the server couldn't rasterize;
 *   the phase still advances so approval isn't trapped.
 *
 * Selection state lives in the shared RHF form values (`chosenCandidate`,
 * `highlightColor` — the SAME fields the approve payload submits); the
 * highlighted swatch/thumbnail is DERIVED from them so the panel stays in
 * sync with any other control editing them. A blank/skip pick mirrors the
 * server's missing/blank-means-skip approval contract. In phase "final" the
 * pick is snapped to the finalized draft (the server rejects a mismatch).
 *
 * Reuse (2026-07-26): when the monday row already carried artwork, nothing was
 * generated and `manifest.reuseStage` says how far along it was. `card` — the
 * Speaker Card itself is on the board, so there is nothing to make: a notice
 * replaces the grid and approval just closes the step. `final` — the finished
 * cut-out was picked up, so the panel opens straight in phase "final" with the
 * re-color and back-to-drafts controls hidden (there is no draft behind it and
 * no halo to repaint; the server rejects a finalize on it). `v1` — a plain
 * draft was picked up and still needs a color, which is the ordinary flow.
 * "Generate new" (action `force_regenerate`) is the one way past all of this.
 *
 * Non-ok manifests keep the gate usable: `no_headshot` explains that the
 * speaker's assets land in Drive automatically, `error` surfaces the failure
 * summary, and `not_generated` (cutover workflows — generation is the
 * reviewer's choice, 2026-07-24) shows a calm empty state whose header
 * button reads "Generate illustrations"; all keep the Regenerate action so
 * the reviewer can (re)generate once ready. The parent owns all mutations
 * and refetches the workflow so the re-persisted gateData re-renders this
 * panel.
 */
import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Button } from '../shared/Button';
import { Icon } from '../../lib/icons';
import { cn } from '../../lib/cn';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import type { CardCandidateManifest, CardGateState } from '../../api/types';

// The gate-schema fields this panel drives (backend contract:
// card_gate.CHOSEN_CANDIDATE_KEY / HIGHLIGHT_COLOR_KEY) and their special
// option values. Approval treats a missing/blank pick as skip.
const CHOSEN_CANDIDATE_FIELD = 'chosenCandidate';
const HIGHLIGHT_COLOR_FIELD = 'highlightColor';
const SKIP_CHOICE = 'skip';
const RANDOM_COLOR = 'random';

/** Public props for CardCandidatesPanel. */
export interface CardCandidatesPanelProps {
  /** The typed manifest read from `gateData.cardCandidates`. */
  manifest: CardCandidateManifest;
  /** The two-phase state read from the gate defaults (`readCardGateState`). */
  cardState: CardGateState;
  /** Workflow id — path segment of the candidate image proxy. */
  workflowId: string;
  /** Speaker/workflow display name, used in the candidates' alt text. */
  subjectName: string;
  /** Read-only view (viewer can't approve) — all controls hidden/disabled. */
  disabled?: boolean;
  /** True while any regenerate-family request is in flight. */
  isRegenerating?: boolean;
  /** Fired when the reviewer asks for a fresh set of drafts. */
  onRegenerate: () => void;
  /**
   * Fired for the explicit "Generate new" escape hatch (`force_regenerate`) —
   * ignores the artwork already on the monday row and paints from scratch.
   * Distinct from `onRegenerate`, which still reuses the board.
   */
  onForceRegenerate: () => void;
  /** Fired to paint the halo: (draft index, palette color name | 'random'). */
  onFinalize: (chosen: string, color: string) => void;
  /**
   * Fired from phase "final" to compose + stage the speaker card
   * (`build_card`) so the reviewer sees it before approving. Also the "Rebuild"
   * action from phase "card".
   */
  onBuildCard: () => void;
  /** Fired from phase "card" to return to the finished portrait. */
  onBackToFinal: () => void;
  /** Fired from phase "final" to return to the draft grid. */
  onBack: () => void;
  className?: string;
}

/** One clickable color swatch (palette entry or the Random dice). */
function Swatch({
  name,
  hex,
  selected,
  disabled,
  onPick,
}: {
  name: string;
  hex: string | null;
  selected: boolean;
  disabled: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      aria-pressed={selected}
      aria-label={`Highlight color ${name}`}
      title={name}
      className={cn(
        'flex items-center gap-1.5 rounded-full border px-2 py-1 text-[11.5px] font-medium outline-none',
        'focus-visible:ring-2 focus-visible:ring-accent',
        selected ? 'border-accent ring-1 ring-accent bg-bg' : 'border-border hover:border-text-subtle',
        disabled && 'cursor-default opacity-60',
      )}
    >
      {hex ? (
        <span
          className="h-3.5 w-3.5 shrink-0 rounded-full border border-black/10"
          style={{ backgroundColor: hex }}
        />
      ) : (
        <Icon name="shuffle" size={12} className="text-text-muted" />
      )}
      {name}
    </button>
  );
}

/**
 * Two-phase card gate panel bound to the shared `chosenCandidate` +
 * `highlightColor` form values. Pure presentation over the RHF context; the
 * parent owns the FormProvider and the regenerate/finalize/back mutations.
 *
 * @sideEffects writes the two form values via setValue on click (and snaps a
 *              stale pick back to the server's state after regenerate/finalize)
 * @idempotent yes
 */
export function CardCandidatesPanel({
  manifest,
  cardState,
  workflowId,
  subjectName,
  disabled = false,
  isRegenerating = false,
  onRegenerate,
  onForceRegenerate,
  onFinalize,
  onBuildCard,
  onBackToFinal,
  onBack,
  className,
}: CardCandidatesPanelProps) {
  const form = useFormContext();
  const isCard = cardState.phase === 'card' && cardState.cardPreview != null;
  const isFinal = !isCard && cardState.phase === 'final' && cardState.finalPreview != null;

  // Single source of truth: the RHF values the approve payload submits.
  // Selection is DERIVED from them (edits via any other control stay in
  // sync); clicks write back through setValue. Blank pick mirrors the
  // server's missing/blank-means-skip approval contract. useWatch (not
  // form.watch) so THIS component holds the subscription — it's established
  // before the snap effects below run, so corrections re-render even when
  // they fire on first mount.
  const watched: unknown = useWatch({ name: CHOSEN_CANDIDATE_FIELD });
  const selected = typeof watched === 'string' ? watched : '';
  const skipSelected = selected === SKIP_CHOICE || selected === '';
  const watchedColor: unknown = useWatch({ name: HIGHLIGHT_COLOR_FIELD });
  const colorChoice = typeof watchedColor === 'string' && watchedColor ? watchedColor : RANDOM_COLOR;

  // Expand-to-review lightbox (Malea R6, 2026-08-18): the grid thumbnails are
  // ~200px and object-cover cropped; the reviewer can open any draft full-size
  // to check detail BEFORE selecting a version. Selecting stays a separate
  // click on the thumbnail — expanding never changes the pick.
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const candidateSrc = (index: number | string, driveFileId?: string | null) =>
    `/api/workflows/${workflowId}/card-candidates/${index}${
      driveFileId ? `?v=${encodeURIComponent(driveFileId)}` : ''
    }`;
  const expandedCandidate =
    expandedIndex == null ? null : (manifest.candidates.find((c) => c.index === expandedIndex) ?? null);

  const validChoices = useMemo(
    () => new Set([...manifest.candidates.map((c) => String(c.index)), SKIP_CHOICE]),
    [manifest.candidates],
  );
  const firstCandidate = manifest.candidates[0];
  const defaultChoice = firstCandidate != null ? String(firstCandidate.index) : SKIP_CHOICE;

  // A regenerate can land a manifest whose indices no longer include the
  // reviewer's earlier pick (fewer candidates, or ok → error). Snap a stale
  // non-empty value back to the fresh default so the approval can't submit
  // an index the server would reject (409 on an unknown pick).
  useEffect(() => {
    if (selected !== '' && !validChoices.has(selected)) {
      form.setValue(CHOSEN_CANDIDATE_FIELD, defaultChoice, { shouldDirty: true });
    }
  }, [selected, validChoices, defaultChoice, form]);

  // Phases "final"/"card": the server only accepts the pick the portrait was
  // painted from (and the card built from) — snap the form value onto it (e.g.
  // a stale localStorage draft) so a build/approve can't 409 on a mismatch.
  const finalizedChoice = cardState.finalPreview?.chosenCandidate ?? '';
  useEffect(() => {
    if ((isFinal || isCard) && finalizedChoice && selected !== finalizedChoice) {
      form.setValue(CHOSEN_CANDIDATE_FIELD, finalizedChoice, { shouldDirty: true });
    }
  }, [isFinal, isCard, finalizedChoice, selected, form]);

  const pick = (choice: string) => {
    if (disabled || choice === selected) return;
    form.setValue(CHOSEN_CANDIDATE_FIELD, choice, { shouldDirty: true });
  };
  const pickColor = (name: string) => {
    if (disabled || name === colorChoice) return;
    form.setValue(HIGHLIGHT_COLOR_FIELD, name, { shouldDirty: true });
  };

  const busy = isRegenerating;
  const preview = cardState.finalPreview;
  const cardPreview = cardState.cardPreview;
  const cardRenderable = isCard && cardPreview != null && cardPreview.status === 'ok';
  // Cutover gates open without generating (user-initiated) — same Regenerate
  // action, calmer labeling.
  const notGenerated = manifest.status === 'not_generated';
  // Reuse rungs: the card is already done (nothing to make), or the finished
  // cut-out was picked up off the board (no draft behind it, no halo to paint).
  const cardOnBoard = manifest.reuseStage === 'card';
  const reusedFinal = preview?.reuseStage === 'final';
  const reused = cardOnBoard || reusedFinal || manifest.reuseStage === 'v1';
  // "Illustrated HS - Final, Speaker Card" — the columns the files sit in.
  const existingColumns = Object.keys(manifest.existing ?? {}).join(', ');

  const swatchRow = (onSwatch: (name: string) => void, current: string) => (
    <div className="flex flex-wrap items-center gap-1.5">
      {cardState.highlightColors.map((c) => (
        <Swatch
          key={c.name}
          name={c.name}
          hex={c.hex}
          selected={current.toLowerCase() === c.name.toLowerCase()}
          disabled={disabled || busy}
          onPick={() => onSwatch(c.name)}
        />
      ))}
      <Swatch
        name="Random"
        hex={null}
        selected={current === RANDOM_COLOR}
        disabled={disabled || busy}
        onPick={() => onSwatch(RANDOM_COLOR)}
      />
    </div>
  );

  return (
    <div className={cn('rounded-lg border border-border bg-bg-soft', className)}>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border flex-wrap">
        <Icon name="user" size={14} className="text-text-muted shrink-0" />
        <span className="text-[13px] font-semibold text-text">
          {cardOnBoard
            ? 'Artwork — already on the board'
            : isCard
              ? 'Step 3 of 3 — the speaker card'
              : isFinal
                ? 'Step 2 of 3 — the finished illustration'
                : 'Step 1 of 3 — illustration proposals'}
        </span>
        {manifest.model && !reused && (
          <span className="text-[11px] text-text-subtle">via {manifest.model}</span>
        )}
        {reused && (
          <span className="text-[11px] text-text-subtle">from the monday board — nothing generated</span>
        )}
        <div className="flex-1" />
        {/* The escape hatch. Only shown when the board actually won, so the
            ordinary Regenerate button keeps its plain meaning everywhere else. */}
        {!disabled && reused && (
          <Button
            type="button"
            size="sm"
            variant="default"
            onClick={onForceRegenerate}
            disabled={busy}
            title="Ignore the artwork already on the monday row and paint a fresh illustration (costs a generation)"
          >
            <Icon name="sparkles" size={12} className={cn(busy && 'animate-spin')} />
            {busy ? 'Working…' : 'Generate new'}
          </Button>
        )}
        {!disabled && !isFinal && !isCard && !cardOnBoard && (
          <Button
            type="button"
            size="sm"
            variant="default"
            onClick={onRegenerate}
            disabled={busy}
            title={
              notGenerated
                ? "Generate illustrated drafts from the speaker's headshot"
                : "Re-run draft generation from the speaker's headshot (your pick snaps to the fresh set)"
            }
          >
            <Icon name={notGenerated ? 'sparkles' : 'retry'} size={12} className={cn(busy && 'animate-spin')} />
            {busy ? 'Working…' : notGenerated ? 'Generate illustrations' : 'Regenerate drafts'}
          </Button>
        )}
        {/* No "Back to drafts" on a reused final — there are no drafts behind
            it, and the grid it would show is the finished cut-out mislabeled. */}
        {!disabled && isFinal && !reusedFinal && (
          <Button type="button" size="sm" variant="default" onClick={onBack} disabled={busy}>
            <Icon name="arrow-left" size={12} />
            Back to drafts
          </Button>
        )}
        {/* Phase "card": step back to the portrait, or rebuild the card (picks
            up a changed name/title/company off the board). */}
        {!disabled && isCard && (
          <>
            <Button type="button" size="sm" variant="default" onClick={onBackToFinal} disabled={busy}>
              <Icon name="arrow-left" size={12} />
              Back to the portrait
            </Button>
            <Button
              type="button"
              size="sm"
              variant="default"
              onClick={onBuildCard}
              disabled={busy}
              title="Re-compose the card (picks up an edited name, title or company)"
            >
              <Icon name="retry" size={12} className={cn(busy && 'animate-spin')} />
              {busy ? 'Working…' : 'Rebuild card'}
            </Button>
          </>
        )}
      </div>

      <div className="px-4 py-3">
        {manifest.status === 'error' ? (
          <div
            role="alert"
            className="flex items-start gap-2.5 px-3 py-2.5 rounded-md bg-failed-soft border border-failed-border text-failed text-[12px] leading-[1.55]"
          >
            <Icon name="alert" size={14} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold mb-0.5">Draft generation failed</div>
              {manifest.error || 'Unknown error'}
            </div>
          </div>
        ) : cardOnBoard ? (
          <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-md bg-completed-soft border border-completed-border text-[12px] leading-[1.55] text-[#2f6650]">
            <Icon name="check" size={14} className="mt-0.5 shrink-0" />
            <span>
              <span className="font-semibold">This speaker&apos;s artwork is already done.</span> A
              speaker card is on the monday row
              {existingColumns ? ` (${existingColumns})` : ''}, so nothing was generated and nothing
              will be re-published. Approve to close this step. Use &ldquo;Generate new&rdquo; above
              only if the existing artwork needs redoing.
            </span>
          </div>
        ) : notGenerated ? (
          <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-md bg-bg-muted border border-border text-text-muted text-[12px] leading-[1.55]">
            <Icon name="info" size={14} className="mt-0.5 shrink-0" />
            <span>
              Illustrations haven&apos;t been generated yet — this speaker was onboarded mid-way,
              so nothing runs automatically. Hit &ldquo;Generate illustrations&rdquo; above when
              you&apos;re ready (it takes a minute or two); approving without generating skips the
              card.
            </span>
          </div>
        ) : manifest.status === 'no_headshot' ? (
          <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-md bg-bg-muted border border-border text-text-muted text-[12px] leading-[1.55]">
            <Icon name="info" size={14} className="mt-0.5 shrink-0" />
            <span>
              No headshot on file yet — assets from the speaker land in Drive automatically, and a
              headshot attached to the speaker&apos;s monday item works too. Regenerate once one is
              in either place.
            </span>
          </div>
        ) : isCard && cardPreview != null ? (
          <>
            {busy && (
              <div
                role="status"
                className="flex items-start gap-2.5 px-3 py-2.5 mb-2.5 rounded-md bg-accent-soft border border-accent-border text-[12px] leading-[1.55]"
              >
                <Icon name="retry" size={14} className="mt-0.5 shrink-0 animate-spin" />
                <span>
                  <span className="font-semibold">Rebuilding…</span> the fresh card replaces this
                  preview when it&apos;s done.
                </span>
              </div>
            )}
            <div className={cn('flex flex-col sm:flex-row gap-4', busy && 'opacity-50 pointer-events-none')}>
              <div className="sm:w-[240px] shrink-0">
                {cardRenderable ? (
                  <img
                    src={`/api/workflows/${workflowId}/card-candidates/card?v=${encodeURIComponent(cardPreview.driveFileId)}`}
                    alt={`Speaker card for ${subjectName}`}
                    loading="lazy"
                    className="w-full rounded-lg border border-border bg-[#f3f3f3] object-contain aspect-[4/5]"
                  />
                ) : (
                  <div className="flex aspect-[4/5] w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-border bg-bg-muted px-3 text-center">
                    <Icon name="alert" size={16} className="text-waiting" />
                    <span className="text-[12px] font-medium text-text-muted">No card preview</span>
                    <span className="text-[11px] leading-[1.4] text-text-subtle">
                      This server can&apos;t render the card image
                    </span>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0 flex flex-col gap-2.5">
                {cardRenderable ? (
                  <div className="flex items-start gap-2 text-[12px] leading-[1.55] text-text-muted">
                    <Icon name="check" size={14} className="mt-0.5 shrink-0 text-completed" />
                    <span>
                      <span className="font-semibold text-text">This is the finished card.</span>{' '}
                      Approving publishes exactly this image — nothing is re-drawn. Not right? Go
                      back to the portrait to re-color it, or rebuild the card after fixing the
                      speaker&apos;s name, title or company on the monday row.
                    </span>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 text-[12px] leading-[1.55] text-text-muted">
                    <Icon name="alert" size={14} className="mt-0.5 shrink-0 text-waiting" />
                    <span>
                      <span className="font-semibold text-text">
                        The card image couldn&apos;t be rendered on this server
                      </span>{' '}
                      (the SVG rasterizer is unavailable). Approving still publishes the illustration
                      deliverables — the card itself will need making by hand.
                    </span>
                  </div>
                )}
                {cardPreview.deliverableName && (
                  <div className="text-[11.5px] text-text-subtle break-words">
                    Publishes as <span className="font-medium text-text-muted">{cardPreview.deliverableName}</span>
                  </div>
                )}
                <p className="text-[12px] text-text-muted mt-auto">
                  Approving publishes the deliverables — Illustrated V1, Final cut-out, PT&nbsp;HS
                  composite, and the Speaker Card — to Drive and their monday board columns (no
                  email).
                </p>
              </div>
            </div>
          </>
        ) : isFinal && preview != null ? (
          <>
            {busy && (
              <div
                role="status"
                className="flex items-start gap-2.5 px-3 py-2.5 mb-2.5 rounded-md bg-accent-soft border border-accent-border text-[12px] leading-[1.55]"
              >
                <Icon name="retry" size={14} className="mt-0.5 shrink-0 animate-spin" />
                <span>
                  <span className="font-semibold">Repainting…</span> the fresh final replaces this
                  preview when it&apos;s done (usually under a minute).
                </span>
              </div>
            )}
            <div className={cn('flex flex-col sm:flex-row gap-4', busy && 'opacity-50 pointer-events-none')}>
              <div className="sm:w-[260px] shrink-0">
                <img
                  src={`/api/workflows/${workflowId}/card-candidates/final?v=${encodeURIComponent(preview.driveFileId)}`}
                  alt={`Finalized portrait for ${subjectName} (${preview.color.name} highlight)`}
                  loading="lazy"
                  className="w-full rounded-lg border border-border bg-[#f3f3f3] object-contain aspect-square p-2"
                />
              </div>
              <div className="flex-1 min-w-0 flex flex-col gap-2.5">
                {reusedFinal ? (
                  <div className="flex items-start gap-2 text-[12px] leading-[1.55] text-text-muted">
                    <Icon name="check" size={14} className="mt-0.5 shrink-0 text-completed" />
                    <span>
                      <span className="font-semibold text-text">
                        Reusing the finished illustration on the monday row
                      </span>
                      {existingColumns ? ` (${existingColumns})` : ''} — no illustration was
                      generated and no halo was painted, so the color is whatever the design team
                      chose.
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-[12.5px]">
                    <span className="text-text-muted">Highlight color:</span>
                    <span
                      className="h-3.5 w-3.5 rounded-full border border-black/10"
                      style={{ backgroundColor: preview.color.hex }}
                    />
                    <span className="font-semibold text-text">{preview.color.name}</span>
                    <span className="text-text-subtle">· from draft {preview.chosenCandidate}</span>
                  </div>
                )}
                {/* No re-color on a reused final: its halo is already baked in,
                    so painting again would ring a ring. The server refuses it. */}
                {!disabled && !reusedFinal && (
                  <>
                    <div className="text-[12px] text-text-muted">Not quite? Repaint in another color:</div>
                    {swatchRow((name) => onFinalize(preview.chosenCandidate, name), preview.color.name)}
                  </>
                )}
                <div className="mt-auto flex flex-wrap items-center gap-2.5">
                  <p className="flex-1 min-w-[160px] text-[12px] text-text-muted">
                    {reusedFinal ? (
                      <>
                        Next: build the speaker card from this image — you&apos;ll see it before
                        anything is published. The Illustrated V1 column is left alone: this is a
                        finished cut-out, not a plain draft.
                      </>
                    ) : (
                      <>
                        Next: build the speaker card from this portrait. You&apos;ll see the finished
                        card before anything is published.
                      </>
                    )}
                  </p>
                  {!disabled && (
                    <Button
                      type="button"
                      size="sm"
                      variant="primary"
                      onClick={onBuildCard}
                      disabled={busy}
                      title="Compose the speaker card so you can review it before publishing"
                    >
                      <Icon name="sparkles" size={12} />
                      Build speaker card
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {busy && (
              <div
                role="status"
                className="flex items-start gap-2.5 px-3 py-2.5 mb-2.5 rounded-md bg-accent-soft border border-accent-border text-[12px] leading-[1.55]"
              >
                <Icon name="retry" size={14} className="mt-0.5 shrink-0 animate-spin" />
                <span>
                  <span className="font-semibold">Working…</span> this usually takes a minute or
                  two — the result replaces these when it&apos;s done.
                </span>
              </div>
            )}
            <p className="text-[12px] text-text-muted mb-2.5">
              {manifest.reuseStage === 'v1' && (
                <>
                  <span className="font-semibold text-text">
                    This illustration was already on the monday row
                  </span>
                  {existingColumns ? ` (${existingColumns})` : ''} — nothing was generated, it just
                  still needs a highlight color.{' '}
                </>
              )}
              Pick the plain illustrated draft, choose a highlight color (or leave it on Random),
              then create the final. You&apos;ll review the colored cut-out and then the finished
              speaker card before anything is published. Choose &ldquo;Skip card&rdquo; if the card
              is handled outside Pixel.
            </p>
            <div
              className={cn(
                'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3',
                busy && 'opacity-50 pointer-events-none',
              )}
            >
              {manifest.candidates.map((c) => {
                const value = String(c.index);
                const isSelected = !skipSelected && selected === value;
                return (
                  <div key={c.index} className="relative">
                    <button
                      type="button"
                      onClick={() => pick(value)}
                      disabled={disabled}
                      aria-pressed={isSelected}
                      aria-label={`Draft ${c.index} for ${subjectName}`}
                      className={cn(
                        'relative block w-full rounded-lg overflow-hidden border text-left outline-none',
                        'focus-visible:ring-2 focus-visible:ring-accent',
                        isSelected
                          ? 'border-accent ring-2 ring-accent'
                          : 'border-border hover:border-text-subtle',
                        disabled && 'cursor-default',
                      )}
                    >
                      <img
                        src={candidateSrc(c.index, c.driveFileId)}
                        alt={`Draft ${c.index} for ${subjectName}`}
                        loading="lazy"
                        className="aspect-square w-full object-cover bg-bg-muted"
                      />
                      <span className="absolute top-1.5 left-1.5 grid place-items-center w-5 h-5 rounded-full bg-bg/85 backdrop-blur-sm border border-border text-[11px] font-semibold text-text">
                        {c.index}
                      </span>
                      {isSelected && (
                        <span className="absolute top-1.5 right-1.5 grid place-items-center w-5 h-5 rounded-full bg-accent text-white">
                          <Icon name="check" size={12} />
                        </span>
                      )}
                    </button>
                    {/* Expand affordance — a sibling (not nested in the select
                        button) so it's valid markup; stays enabled for viewers
                        so read-only reviewers can inspect too. */}
                    <button
                      type="button"
                      onClick={() => setExpandedIndex(c.index)}
                      aria-label={`Expand draft ${c.index} to review detail`}
                      title="Expand to review detail"
                      className={cn(
                        'absolute bottom-1.5 right-1.5 grid place-items-center w-6 h-6 rounded-full outline-none',
                        'bg-bg/85 backdrop-blur-sm border border-border text-text-muted',
                        'hover:text-text hover:border-text-subtle focus-visible:ring-2 focus-visible:ring-accent',
                      )}
                    >
                      <Icon name="maximize" size={12} />
                    </button>
                  </div>
                );
              })}
              <button
                type="button"
                onClick={() => pick(SKIP_CHOICE)}
                disabled={disabled}
                aria-pressed={skipSelected}
                className={cn(
                  'relative rounded-lg border border-dashed outline-none aspect-square',
                  'flex flex-col items-center justify-center gap-1.5 px-2',
                  'focus-visible:ring-2 focus-visible:ring-accent',
                  skipSelected
                    ? 'border-accent ring-2 ring-accent bg-bg-muted'
                    : 'border-border hover:border-text-subtle',
                  disabled && 'cursor-default',
                )}
              >
                <Icon name="x" size={16} className="text-text-subtle" />
                <span className="text-[12px] font-medium text-text-muted">Skip card</span>
                <span className="text-[11px] text-text-subtle text-center leading-[1.4]">
                  Card handled outside Pixel — publish nothing
                </span>
                {skipSelected && (
                  <span className="absolute top-1.5 right-1.5 grid place-items-center w-5 h-5 rounded-full bg-accent text-white">
                    <Icon name="check" size={12} />
                  </span>
                )}
              </button>
            </div>
            {!disabled && (
              <div className="mt-3 flex flex-wrap items-center gap-2.5 rounded-md border border-border bg-bg px-3 py-2.5">
                <span className="text-[12px] font-medium text-text-muted">Highlight color:</span>
                {swatchRow(pickColor, colorChoice)}
                <div className="flex-1" />
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  onClick={() => onFinalize(selected, colorChoice)}
                  disabled={busy || skipSelected}
                  title={
                    skipSelected
                      ? 'Pick a draft first (or approve with Skip to publish nothing)'
                      : 'Paint the highlight and cut the portrait out — you review it before publishing'
                  }
                >
                  <Icon name="sparkles" size={12} />
                  Create final portrait
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <Dialog
        open={expandedCandidate != null}
        onOpenChange={(open) => {
          if (!open) setExpandedIndex(null);
        }}
      >
        {expandedCandidate && (
          <DialogContent className="w-[min(560px,92vw)] max-w-[min(560px,92vw)]">
            <DialogHeader>
              <DialogTitle>
                Draft {expandedCandidate.index} · {subjectName}
              </DialogTitle>
            </DialogHeader>
            <div className="px-5 pb-5">
              <img
                src={candidateSrc(expandedCandidate.index, expandedCandidate.driveFileId)}
                alt={`Draft ${expandedCandidate.index} for ${subjectName}, enlarged`}
                className="w-full rounded-lg border border-border bg-bg-muted object-contain"
              />
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
